import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../../context/LangContext';
import { useVisitorCount } from '../../hooks/useVisitorCount';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';
const T = { fr, en, de };

const NAV_LINKS = [
  { num: 'I',   key: 'tsanga',  id: 'hero'    },
  { num: 'II',  key: 'stills',  id: 'gallery' },
  { num: 'III', key: 'record',  id: 'record'  },
  { num: 'IV',  key: 'lives',   id: 'lives'   },
  { num: 'V',   key: 'merch',   id: 'shop'    },
  { num: 'VI',  key: 'contact', id: 'contact' },
];

function BrusselsTime() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const fmt = () =>
      setTime(new Intl.DateTimeFormat('fr-BE', {
        timeZone: 'Europe/Brussels',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      }).format(new Date()));
    fmt();
    const id = setInterval(fmt, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span style={{
      fontFamily: 'var(--mono)', fontSize: 12,
      letterSpacing: '0.14em', color: 'var(--muted)',
      fontVariantNumeric: 'tabular-nums',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: '#fff', opacity: 0.7,
        boxShadow: '0 0 6px rgba(255,255,255,0.5)',
        display: 'inline-block',
        animation: 'navPulse 2.4s ease-in-out infinite',
      }} />
      BXL {time}
    </span>
  );
}

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const { lang, setLang } = useLang();
  const t = T[lang] || T.fr;
  const liveCount = useVisitorCount();
  // Pad live count to 6 digits for the LCD aesthetic; show ------ while loading
  // so the layout width never jumps when the API resolves.
  const visitors =
    liveCount === null
      ? '------'
      : String(liveCount).padStart(6, '0').slice(-6);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <>
      <style>{`
        @keyframes navPulse {
          0%,100% { opacity: 0.4; }
          50%      { opacity: 1;   }
        }
      `}</style>
      <motion.nav
        style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          height: 64, padding: '0 32px', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: scrolled ? 'rgba(4,4,10,0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          borderBottom: scrolled ? '1px solid var(--line)' : 'none',
          transition: 'background 0.4s, border-color 0.4s',
        }}
        aria-label="Main navigation"
      >
        {/* Logo */}
        <button
          onClick={() => scrollTo('hero')}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'none' }}
          aria-label="Go to home"
        >
          <img
            src="/tsangalogosansfond.png"
            alt="TSANGA"
            style={{ height: 22, mixBlendMode: 'screen', opacity: 0.9 }}
          />
        </button>

        {/* Links */}
        <div className="nav-links" style={{ display: 'flex', gap: 20, alignItems: 'center', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {NAV_LINKS.map(({ num, key, id }) => (
            <motion.button
              key={id}
              onClick={() => scrollTo(id)}
              style={{
                background: 'none', border: 'none',
                fontFamily: 'var(--mono)', fontSize: 10,
                letterSpacing: '0.16em', color: 'var(--muted)',
                textTransform: 'uppercase',
                display: 'flex', gap: 6, alignItems: 'baseline',
                cursor: 'none',
              }}
              whileHover={{ color: '#fff' }}
              transition={{ duration: 0.2 }}
            >
              <span style={{ color: 'var(--faint)', fontSize: 9 }}>{num}</span>
              <span className="nav-label">{t.nav[key]}</span>
            </motion.button>
          ))}
        </div>

        {/* Right: visitors + clock + lang */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <div className="nav-visitors" style={{
            display: 'flex', alignItems: 'center', gap: 8,
            border: '1px solid var(--line)', padding: '4px 10px', borderRadius: 100,
          }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.2em', color: 'var(--faint)', textTransform: 'uppercase' }}>
              Visitors
            </span>
            <span style={{ display: 'flex', gap: 2 }}>
              {visitors.split('').map((d, i) => (
                <span key={i} style={{
                  display: 'inline-grid', placeItems: 'center',
                  width: 14, height: 18,
                  fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500,
                  color: '#fff',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {d}
                </span>
              ))}
            </span>
          </div>

          <span className="nav-clock"><BrusselsTime /></span>

          <div style={{ display: 'flex', gap: 8, fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em' }}>
            {['FR', 'EN', 'DE'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l.toLowerCase())}
                style={{
                  color: lang.toUpperCase() === l ? '#fff' : 'var(--faint)',
                  fontWeight: lang.toUpperCase() === l ? 500 : 300,
                  transition: 'color 0.2s',
                  background: 'none', border: 'none', padding: 0,
                  cursor: 'none',
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </motion.nav>
    </>
  );
}
